import {
  TabProps,
  User,
} from "@/components/SettingsSidepanel/settings.types.ts";
import SettingsSheetHeader from "@/components/SettingsSidepanel/SettingsSheetHeader.tsx";
import SettingsSheetProfile from "@/components/SettingsSidepanel/SettingsSheetProfile.tsx";
import { Separator } from "@/components/ui/separator.tsx";

import {
  ProjectOfficesQuery,
  UsersQuery,
  UsersQueryVariables,
} from "@/__generated__/graphql";
import { Loader } from "@/components/common/Loader.tsx";
import { InfiniteScroll } from "@/components/common/InfiniteScroll.tsx";
import LongContentWrapper from "@/components/common/LongContentWrapper.tsx";
import InviteUsers from "@/components/SettingsSidepanel/Users/InviteUsers.tsx";
import PendingInvitations from "@/components/SettingsSidepanel/Users/PendingInvitations.tsx";
import { Button } from "@/components/ui/button.tsx";
import SearchBar from "@/components/ui/search-bar.tsx";
import { useUserContext } from "@/contexts/UserContextProvider.tsx";
import { USERS_QUERY } from "@/graphql/queries/common/UsersQuery.ts";
import { MONTH_DAY_YEAR_FORMAT, userRoles } from "@/helpers/constants.ts";
import { formatDate } from "@/helpers/date.ts";
import { useQuery } from "@apollo/client";
import { ChevronDown, Pencil } from "lucide-react";
import React, { useState } from "react";
import EditUser from "@/components/SettingsSidepanel/Users/EditUser.tsx";
import { UserRoundSearch, Shield, User as UserIcon } from "lucide-react";
import { UserRole } from "@/__generated__/graphql";
import { cn } from "@/lib/utils";
import { useDebounce } from "use-debounce";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { gql } from "@apollo/client";

const PROJECT_OFFICES_QUERY = gql`
  query ProjectOffices {
    projectOffices {
      id
      name
      company {
        id
      }
    }
  }
`;

const PAGE_SIZE = 20;

const userIcons = {
  [UserRole.Admin]: <Shield className="size-4 text-yellow-500" />,
  [UserRole.Collaborator]: <UserIcon className="size-4 text-blue-500" />,
  [UserRole.Reviewer]: <UserRoundSearch className="size-4 text-green-500" />,
};

const UsersTab: React.FC<TabProps> = ({ onRequestClose }) => {
  const [showPending, setShowPending] = useState(false);
  const [inviteUsersComponent, setInviteUsersComponent] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [officeSearch, setOfficeSearch] = useState("");
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);

  const [searchEmail, setSearchEmail] = useState("");
  const [debouncedSearch] = useDebounce(searchEmail, 300);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { currentUser } = useUserContext();

  const projectOffices = useQuery<ProjectOfficesQuery>(PROJECT_OFFICES_QUERY);

  const { loading, error, data, previousData, fetchMore } = useQuery<
    UsersQuery,
    UsersQueryVariables
  >(USERS_QUERY, {
    variables: {
      officeId: selectedOfficeId,
      search: debouncedSearch.trim() || null,
      first: PAGE_SIZE,
      offset: 0,
      isPending: false,
    },
    fetchPolicy: "cache-and-network",
  });

  const currentData = data ?? previousData;

  if (loading && !currentData) {
    return (
      <div className="flex items-center h-full">
        <Loader />
      </div>
    );
  }

  if (error && !currentData) {
    return (
      <div className="text-red-500 text-center py-6">
        Error Loading Users: {error.message}
      </div>
    );
  }

  const joinedUsers: User[] = currentData?.users ?? [];
  const pendingCount = currentData?.pendingCount ?? 0;
  const totalCount = currentData?.usersCount ?? 0;
  const hasMore = joinedUsers.length < totalCount;

  const handleOpenPending = () => {
    setShowPending(true);
  };

  const loadMore = async () => {
    if (loading || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          offset: joinedUsers.length,
          first: PAGE_SIZE,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;
          return {
            ...previousResult,
            users: [
              ...(previousResult.users || []),
              ...(fetchMoreResult.users || []),
            ],
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {!showPending && !inviteUsersComponent && !selectedUser && (
        <>
          <SettingsSheetHeader onRequestClose={onRequestClose} title="Users" />
          <div className="flex flex-col gap-4 p-4 rounded-2xl border border-accent-foreground overflow-auto">
            <SettingsSheetProfile />
            <Separator />

            <div className="flex items-center justify-between gap-2 bg-primary">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Button
                  variant="paint"
                  className="shrink-0 h-9 px-3"
                  onClick={() => setInviteUsersComponent(true)}
                >
                  + Users
                </Button>
                <SearchBar
                  placeholder="Search by email..."
                  searchText={searchEmail}
                  setSearchText={setSearchEmail}
                  containerClassName="flex-1 min-w-[140px] max-w-[220px]"
                  className="h-9 text-xs"
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="h-9 min-w-32.5 max-w-45 border justify-between text-secondary-foreground font-normal px-2.5 text-xs">
                      <span className="truncate mr-1.5">
                        {selectedOfficeId
                          ? projectOffices?.data?.projectOffices.find(
                              (o) => o.id === selectedOfficeId,
                            )?.name
                          : "All Offices"}
                      </span>
                      <ChevronDown className="size-3.5 shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    className="text-secondary-foreground max-h-60 min-w-65 p-0"
                    align="end"
                  >
                    <div className="sticky top-0 z-10 bg-popover p-2 border-b border-border shadow-xs">
                      <input
                        type="text"
                        placeholder="Search office..."
                        className="office-search-input w-full border rounded px-2 py-1 text-sm bg-background text-foreground"
                        value={officeSearch}
                        onChange={(e) => setOfficeSearch(e.target.value)}
                        onKeyDownCapture={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUpCapture={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="p-1">
                      <DropdownMenuItem
                        onClick={() => setSelectedOfficeId(null)}
                      >
                        All Offices
                      </DropdownMenuItem>
                      {[...(projectOffices?.data?.projectOffices || [])]
                        .filter((office) =>
                          office.name
                            .toLowerCase()
                            .includes(officeSearch.toLowerCase()),
                        )
                        .sort((a, b) =>
                          (a.name || "").localeCompare(
                            b.name || "",
                            undefined,
                            {
                              sensitivity: "base",
                            },
                          ),
                        )
                        .map((office) => (
                          <DropdownMenuItem
                            key={office.id}
                            className={cn(
                              "cursor-pointer flex items-center gap-2",
                              selectedOfficeId === office.id &&
                                "bg-accent-foreground",
                            )}
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedOfficeId(office.id);
                            }}
                          >
                            <span className="truncate">{office.name}</span>
                          </DropdownMenuItem>
                        ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="paint"
                  className="shrink-0 h-9 px-3 text-xs whitespace-nowrap"
                  disabled={pendingCount === 0}
                  onClick={handleOpenPending}
                >
                  Pending: {pendingCount}
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-primary">
              <div
                id="users-table-header"
                className="bg-accent text-secondary-foreground sticky top-0 z-10 flex w-full p-4 text-base font-medium"
              >
                <p className="w-1/4">Name</p>
                <p className="w-1/6">Joined on </p>
                <p className="w-1/5">Invite by</p>
                <p className="w-1/5">Role</p>
                <p className="w-1/5">Status</p>
              </div>

              <div id="users-table-body">
                {joinedUsers.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    {debouncedSearch
                      ? "No users matching your email search."
                      : "No users found."}
                  </div>
                ) : (
                  <InfiniteScroll
                    hasMore={hasMore}
                    loader={
                      <div className="py-3 text-center text-sm text-muted-foreground">
                        <Loader size={18} />
                      </div>
                    }
                    loadMore={loadMore}
                  >
                    {joinedUsers.map((user: User, index) => (
                      <div
                        key={user.id || index}
                        className="border-ring hover:bg-secondary/50 text-foreground flex w-full border-b p-4 text-sm font-light"
                      >
                        <p className="w-1/4 flex items-center gap-1">
                          {userIcons[user.role]}
                          <LongContentWrapper>
                            {user.firstName} {user.lastName}
                          </LongContentWrapper>
                        </p>
                        <p className="w-1/6">
                          {formatDate(user.dateJoined, MONTH_DAY_YEAR_FORMAT)}
                        </p>
                        <p className="w-1/5">
                          <LongContentWrapper>
                            {user.inviter?.firstName} {user.inviter?.lastName}
                          </LongContentWrapper>
                        </p>
                        <p className="w-1/5">{userRoles[user.role]}</p>
                        <p
                          className={cn(
                            "w-1/6",
                            user.isActive ? "text-green-500" : "text-red-500",
                          )}
                        >
                          {user.isActive ? "Active" : "Deactivated"}
                        </p>
                        <p>
                          {currentUser?.id !== user.id && (
                            <Pencil
                              className="size-4 cursor-pointer hover:text-primary-foreground/50"
                              onClick={() => setSelectedUser(user)}
                            />
                          )}
                        </p>
                      </div>
                    ))}
                  </InfiniteScroll>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {showPending && (
        <PendingInvitations
          officeId={selectedOfficeId}
          onBack={() => setShowPending(false)}
          onRequestClose={onRequestClose}
        />
      )}

      {inviteUsersComponent && currentUser?.office && (
        <InviteUsers
          onBack={() => setInviteUsersComponent(false)}
          onRequestClose={onRequestClose}
          office={currentUser.office}
          offices={projectOffices.data?.projectOffices || []}
        />
      )}

      {selectedUser && (
        <EditUser
          user={selectedUser}
          onRequestClose={onRequestClose}
          onBack={() => setSelectedUser(null)}
          offices={projectOffices.data?.projectOffices || []}
        />
      )}
    </div>
  );
};

export default UsersTab;
